import React from 'react';
import { Maximize2 } from 'lucide-react';
import { moduleById } from '../../data/skillModules';
import { AudienceLevel, Cohort } from '../../types/pathway';
import { PathwayDocument, pageCount } from './PathwayDocument';
import { PowerPointPreview } from './PowerPointPreview';
export type ShareFormat = 'pdf' | 'link' | 'word' | 'csv' | 'powerpoint';
interface ShareFormatPreviewProps {
  format: ShareFormat;
  pathwayName: string;
  company: string;
  cohort: Cohort | null;
  audience: AudienceLevel | 'any';
  items: string[];
  fields: string[];
  onExpand: () => void;
}
export function ShareFormatPreview({
  format,
  pathwayName,
  company,
  cohort,
  audience,
  items,
  fields,
  onExpand
}: ShareFormatPreviewProps) {
  const isDocument = format === 'pdf' || format === 'word';
  const heading = format === 'powerpoint' ? 'PowerPoint preview' : format === 'csv' ? 'CSV preview' : format === 'link' ? 'Shared page preview' : 'Document preview';
  if (format === 'powerpoint') {
    return <PreviewFrame heading={heading} data-id="element-1985" data-mp="3pnwrxsxj79gc">
        <PowerPointPreview pathwayName={pathwayName} company={company} audience={audience} items={items} data-id="element-1986" data-mp="3lmbd421fsl3i" />
        <p className="mt-3 text-xs text-gray-500" data-id="element-1987" data-mp="3hq8jq5619gt">
          Slide deck with a cover, pathway overview, and one editable slide per module.
        </p>
      </PreviewFrame>;
  }
  if (format === 'csv') {
    return <PreviewFrame heading={heading} data-id="element-1988" data-mp="ceqrhh9gufcv">
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white text-left shadow-sm" data-id="element-1989" data-mp="8gb4vehar8zz">
          <div className="grid grid-cols-[2rem_minmax(0,1fr)] border-b border-gray-200 bg-gray-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1990" data-mp="m5v62fv8by76">
            <span data-id="element-1991" data-mp="w4kny8n4linf">#</span><span data-id="element-1992" data-mp="w4aziphqbjju">Skill module</span>
          </div>
          {items.slice(0, 5).map((id, index) => <div key={id} className="grid grid-cols-[2rem_minmax(0,1fr)] border-b border-gray-100 px-3 py-2 text-xs last:border-b-0" data-id="element-1993" data-mp="r72dgdtgmvdb" data-mpi={index}>
              <span className="font-medium text-gray-500" data-id="element-1994" data-mp="yb0rgde7v9by">{index + 1}</span>
              <span className="truncate font-medium text-gray-900" data-id="element-1995" data-mp="1392ch3v7diq2">{moduleById(id)?.name}</span>
            </div>)}
        </div>
        <p className="mt-3 text-xs text-gray-500" data-id="element-1996" data-mp="185kcjqfl8sw2">
          A clean import file with pathway details and selected columns.
        </p>
      </PreviewFrame>;
  }
  return <PreviewFrame heading={heading} action={isDocument ? onExpand : undefined} data-id="element-1997" data-mp="1hwx4ei1dzj23">
      <button type="button" onClick={isDocument ? onExpand : undefined} disabled={!isDocument} aria-label={isDocument ? 'Open full document preview' : undefined} className="mt-3 flex w-full justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] disabled:cursor-default" data-id="element-1998" data-mp="1lxgzzh6baem4">
        <div className="h-[412px] w-[318px] overflow-hidden rounded-lg border border-gray-200 bg-white" data-id="element-1999" data-mp="3srku3z7te2s7">
          <div className="w-[816px] origin-top-left scale-[0.39]" data-id="element-2000" data-mp="3q4tjmp7yj87k">
            <PathwayDocument pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={fields} coverOnly data-id="element-2001" data-mp="3q64nep1ekfyo" />
          </div>
        </div>
      </button>
      <p className="mt-3 text-xs text-gray-500" data-id="element-2002" data-mp="3bhqnalvdf5lt">
        {format === 'link' ? 'A branded shared page with the pathway in the recommended order.' : `${pageCount(items.length)} pages · cover plus the modules in order.`}
      </p>
    </PreviewFrame>;
}
function PreviewFrame({
  heading,
  action,
  children
}: {
  heading: string;
  action?: () => void;
  children: React.ReactNode;
}) {
  return <div className="rounded-xl border border-gray-200 bg-gray-50 p-5" data-id="element-2003" data-mp="3u8ricytdeh03">
      <div className="flex items-center justify-between gap-3" data-id="element-2004" data-mp="2nrw8vzvel8zg">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-2005" data-mp="2iqyttw5owv26">{heading}</span>
        {action && <button type="button" onClick={action} className="flex items-center gap-1 text-sm font-medium text-[#DB3725] transition-colors hover:text-[#c02f1f]" data-id="element-2006" data-mp="begs9ya8m5m6">
            <Maximize2 size={14} data-id="element-2007" data-mp="1jotws8tzeqc" />
            View full document
          </button>}
      </div>
      {children}
    </div>;
}